import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  DeleteForeverRounded as DeleteForeverRoundedIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useFormik } from "formik";
import { formatData as Utils } from '../../utils/format';
import { ONE_MB_SIZE_IN_BYTES } from '../../utils/constants';
import { Checkbox } from '../../components/Checkbox';
import { Dropzone } from '../../components/Dropzone';
import { DatePicker } from '../../components/DatePicker';
import { useToast } from '../../hooks/useToast';
import { Autocomplete } from '../Autocomplete';
import { useStyles } from './styles';
import { 
  ChatbotPostPutCalendarObject, 
  CalendarObject, 
  Instance, 
  Gatilho, 
  ChatbotDelTriggerObject
} from '../../types/whatsapp';
import moment, { Moment } from 'moment';
import * as Yup from 'yup';

interface CalendarDialogProps {
  open: boolean;
  calendario: CalendarObject | null;
  instances: Array<Instance>;
  instanceSelected: Instance | null;
  gatilhos: Gatilho[] | null;
  hasTranshipment: boolean;
  loading: boolean;
  loadingGatilhos: boolean;
  onClose: () => void;
  onSave: (calendar: ChatbotPostPutCalendarObject, gatilhos: Gatilho[]) => void;
  onAddGatilho?: () => void;
  onDelGatilho: (trigger: ChatbotDelTriggerObject) => void;
  onUpdateGatilho?: () => void;
};

const VALIDATION_SCHEMA = Yup.object().shape({
  titulo: Yup.string().required('Informe o título'),
  palavra_chave: Yup.string().required('Informe a palavra chave'),
  webhook: Yup.boolean().required(''),
  webhook_endereco: Yup.string().when('webhook', {
    is: true,
    then: Yup.string()
      .test('is-empty', 'Informe o endereço do webhook', 
        value => typeof value === 'string' && value.trim().length > 0
      )
      .test('is-url', 'Informe uma URL válida', 
        value => typeof value === 'string' && Yup.string().url().isValidSync(value)
      )
  })
});

export function CalendarDialog({
  open,
  onClose,
  onSave,
  calendario,
  instances,
  instanceSelected,
  hasTranshipment,
  onDelGatilho,
  loading,
  loadingGatilhos,
  gatilhos
}: CalendarDialogProps){
  const classes = useStyles();
  const [expanded, setExpanded] = useState<string | boolean>('trigger0');
  const toast = useToast();
  const formik = useFormik({
    initialValues: {
      id: calendario?.id || null,
      titulo: calendario?.titulo || '',
      descricao: calendario?.descricao || '',
      id_instancia: calendario?.id_instancia || null,
      palavra_chave: calendario?.palavra_chave || '',
      ativo: calendario?.ativo || 1,
      data_inicio: calendario?.data_inicio || Utils( moment(), 'YYYY-MM-DD') ,
      data_final: calendario?.data_final || Utils(moment().add(1,'month'), 'YYYY-MM-DD'),
      recorrente: calendario?.recorrente || 0,
      created_at: calendario?.created_at || null,
      updated_at: calendario?.updated_at || null,
      dia_semana: calendario?.dia_semana || null,
      horario_inicio: calendario?.horario_inicio || null,
      transbordo: calendario?.transbordo || 0,
      webhook: calendario?.webhook || 0,
      webhook_endereco: calendario?.webhook_endereco || '',
      gatilhos: [{ id: null, id_calendario: null, url: '', mensagem: '' }],
      instanciaSelected: instanceSelected || null,
      activeStep: 0
    },
    onSubmit: () => {},
    validationSchema: VALIDATION_SCHEMA,
    validateOnBlur: true
  });

  useEffect(() => {
    if(gatilhos){
      formik.setFieldValue('gatilhos',gatilhos);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatilhos]);

  const handleCalendarFormik = (
    attr: string, 
    value: ( boolean | number | Date | string | null | Moment | Array<Gatilho> | Gatilho )
  ) => {
    formik.setFieldValue( attr, value);
  };

  const handleDeleteGatilho = (index: number) => {
    const gatilhos = formik.values.gatilhos;
    const gatilhoRemovido = gatilhos.splice(index,1);
    handleCalendarFormik('gatilhos', [...gatilhos]);
    if(gatilhoRemovido[0].id){
      onDelGatilho({ trigger_id: gatilhoRemovido[0].id })
    }
  };

  const handleGatilhos = (index: number, attr: string, value: any) => {
    const newGatilhos = formik.values.gatilhos;
    if(attr==='url'){
      newGatilhos[index].url = value;
    }else{
      newGatilhos[index].mensagem = value;
    }
    handleCalendarFormik('gatilhos', [...newGatilhos]);
  };

  const novoGatilho = () => {
    let lastGatilho;
    if (formik.values.gatilhos.length > 0) {
      lastGatilho = formik.values.gatilhos[formik.values.gatilhos.length - 1];
      /*if ((lastGatilho.url || lastGatilho.mensagem) && formik.values.id) {
        if (lastGatilho.id) {
          console.log('gatilho para atualizar::>',lastGatilho);
          //onUpdateGatilho(lastGatilho);
        } else {
          console.log('Gatinho para criar::>',lastGatilho);
          //onAddGatilho({ calendario, gatilho: lastGatilho });
        }
      }*/
    }
    if ((lastGatilho && (lastGatilho.url || lastGatilho.mensagem)) || formik.values.gatilhos.length === 0) {
      const newGatilhos = [...formik.values.gatilhos, { id: null, id_calendario: null, url: '', mensagem: '' }];
      handleCalendarFormik('gatilhos', newGatilhos);
      setExpanded(`trigger${newGatilhos.length - 1}`);
    }
  }

  const getSteps = () => {
    return ['Dados gerais', 'Gatilhos'];
  };

  const handleBack = useCallback(() => {
    const step = formik.values.activeStep - 1;
    formik.setFieldValue('activeStep', step < 0 ? 0 : step)
  },[formik]);

  const handleNext = useCallback(() => {
    let step = formik.values.activeStep;
    if(Object.keys(formik.errors).length){
      return
    }
    step = formik.values.activeStep + 1;
    if (step >= getSteps().length) {      
      const isGatilhosValid = formik.values.gatilhos.filter(
        (gatilho) => gatilho.mensagem || gatilho.url
      );
      
      if(!formik.values.gatilhos.length){
        toast.show('Por favor adicione pelo menos um gatilho', { severity: "error" });
        return
      } 
      if(isGatilhosValid.length < formik.values.gatilhos.length){
        toast.show('Por favor preencha todos os gatilhos', { severity: "error" });
        return
      }
      const calendarObject: ChatbotPostPutCalendarObject = {
        calendar_id: formik.values.id,
        start_date: formik.values.data_inicio,
        end_date: formik.values.data_final,
        title: formik.values.titulo,
        description: formik.values.descricao,
        keyword: formik.values.palavra_chave,
        instance_id: formik.values.instanciaSelected?.id!,
        active: formik.values.ativo,
        transhipment: formik.values.transbordo,
        webhook: formik.values.webhook,
        address_webhook: formik.values.webhook_endereco,
      };
      onSave(calendarObject, isGatilhosValid);
      step = getSteps().length - 1;
    }
    formik.setFieldValue('activeStep',step);
  },[formik, onSave, toast]);

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Grid container direction="row" spacing={2} >
              <Grid item xs={6}>
                <Autocomplete
                  fullWidth
                  name='instanciaSelected'
                  label="Instância"
                  options={instances}
                  value={formik.values.instanciaSelected}
                  getOptionLabel={(instance) => instance.nome}
                  onChange={(_, instance) => formik.setFieldValue('instanciaSelected',instance)}
                  disabled
                />
              </Grid>

              <Grid item xs={3}>
                <DatePicker
                  fullWidth
                  label="De"
                  sx={{ marginRight: '15px'}}
                  value={formik.values.data_inicio.toString()}
                  onChange={date => handleCalendarFormik('data_inicio', date)}
                />
              </Grid>
              <Grid item xs={3}>
                <DatePicker
                  fullWidth
                  label="Até"
                  sx={{ marginRight: '15px' }}
                  value={moment.utc(formik.values.data_final).format('YYYY-MM-DD')}
                  onChange={date => handleCalendarFormik('data_final', date)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name='titulo'
                  label="Titulo"
                  variant="outlined"
                  value={formik.values.titulo}
                  onChange={e => handleCalendarFormik('titulo', e.target.value)}
                  error={formik.errors.hasOwnProperty('titulo')}
                  helperText={formik.errors?.titulo}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="descricao"
                  label="Descrição"
                  variant="outlined"
                  multiline
                  rows="7"
                  value={formik.values.descricao}
                  onChange={e => handleCalendarFormik('descricao', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  name='palavra_chave'
                  label="Palavra chave"
                  value={formik.values.palavra_chave}
                  onChange={e => handleCalendarFormik('palavra_chave', e.target.value)}
                  error={formik.errors.hasOwnProperty('palavra_chave')}
                  helperText={formik.errors?.palavra_chave}
                />
              </Grid>
              <Grid item xs={3}>
                <Tooltip
                  placement="top"
                  title={
                    hasTranshipment
                      ? 'A opção de transbordo está desativada pois já existe um gatilho de transbordo ativa.'
                      : 'Abrir um chamado para um agente.'
                  }
                >
                  <FormControlLabel
                    label='Transbordo Humano'
                    className={classes.formControlLabel}
                    control={
                      <Checkbox
                        disabled={hasTranshipment}
                        onChange={(_, checked) => handleCalendarFormik('transbordo', Number(checked))}
                        value={formik.values.transbordo}
                        checked={Boolean(formik.values.transbordo)}
                      />
                    }
                  />
                </Tooltip> 
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  label='Webhook'
                  className={classes.formControlLabel}
                  control={
                    <Checkbox
                      onChange={(_, checked) => handleCalendarFormik('webhook', Number(checked))}
                      value={formik.values.webhook}
                      checked={Boolean(formik.values.webhook)}
                    />
                  }
                />
              </Grid>
              {Boolean(formik.values.webhook) && (
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Endereço do webhook"
                    value={formik.values.webhook_endereco}
                    onChange={({ target: { value } }) => handleCalendarFormik('webhook_endereco', value)}
                    error={formik.errors.hasOwnProperty('webhook_endereco')}
                    helperText={formik.errors?.webhook_endereco}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box width="100%">
            {formik.values.gatilhos.map((gatilho: any, index: number) => (
              <Accordion
                key={index}
                square
                classes={{ root: classes.accordion, expanded: undefined }}
                expanded={expanded === `trigger${index}`}
                onChange={(_, isExpanded) => setExpanded(Boolean(isExpanded) ? `trigger${index}` : false)}>
                <AccordionSummary
                  key={index}
                  classes={{
                    root: classes.accordionSummary,
                    content: classes.accordionSummaryContent,
                    expanded: undefined,
                  }}
                  aria-controls={`trigger${index}-content`}
                  id={`trigger${index}-header`}>
                  <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between">
                    <Typography>{`#Gatilho ${index + 1}`}</Typography>
                    {index >= 0 && (
                      <IconButton
                        aria-label="remover"
                        onClick={() => {
                          handleDeleteGatilho(index);
                        }}
                      >
                        <DeleteForeverRoundedIcon />
                      </IconButton>
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails classes={{ root: classes.accordionDetails }}>
                    <Grid container flexDirection={'column'} spacing={2}>
                     <Grid item xs={12}>
                        {<Dropzone
                          readonly={loading}
                          variant="two"
                          filesLimit={1}
                          maxFileSize={ONE_MB_SIZE_IN_BYTES * 50}
                          showFileNames={false}
                          clearOnUnmount={false}
                          showFileNamesInPreview={false}
                          dropzoneParagraphClass={`ds-text`}
                          dropzoneText="Arraste e solte algum arquivo ou clique para adicionar"
                          initialFiles={formik.values.gatilhos[index].url ? [formik.values.gatilhos[index].url] : []}
                          acceptedFiles={['image/jpeg', 'image/jpg', 'image/png']}
                          onChange={(files: any) => {
                            if (files.length) {
                              handleGatilhos(index, 'url', files[0]);
                            }else if(files){
                              handleGatilhos(index, 'url', null);
                            }
                          }}
                        />}
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="nome"
                          label="Mensagem"
                          variant="outlined"
                          multiline
                          rows="5"
                          value={formik.values.gatilhos[index].mensagem}
                          onChange={e => handleGatilhos(index, 'mensagem', e.target.value)}
                          disabled={loading}
                        />
                      </Grid>
                    </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );
      default:
        return <Box minHeight={350}>#Resumo</Box>;
    }
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      
      <DialogTitle className={classes.dialogTitle}>
        <Grid container flexDirection="column" >
          <Grid item mb={2}>
            {calendario?.id ? 'Atualizar' : 'Nova'} Resposta Automática
          </Grid>
          <Grid item>
            <Stepper alternativeLabel activeStep={formik.values.activeStep}>
              {getSteps().map((label, index) => (
                <Step key={index}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
            {formik.values.activeStep === 1 && (
              <Grid item>
                <Button
                  onClick={() => {novoGatilho()} }
                  color="primary"
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}>
                  Adicionar gatilho
                </Button>
              </Grid>
            )}
          <Grid item>{(loading || loadingGatilhos) && <LinearProgress />}</Grid>
        </Grid>

        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

      </DialogTitle>
      
      <DialogContent style={{padding: '1rem'}}>
          {getStepContent(formik.values.activeStep)}
      </DialogContent>

      <DialogActions>
        {formik.values.activeStep > 0 && (
          <Button onClick={() => handleBack()} color="primary" variant="outlined">
            Anterior
          </Button>
        )}
        <Button disabled={loading || loadingGatilhos} onClick={async () => {
          handleNext();
        }} color="primary" variant="contained">
          {formik.values.activeStep === getSteps().length - 1 ? 'Salvar' : 'Próximo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};